'use client';

import { useRef, useEffect, useState } from 'react';
import { IconSendMessage } from '@repo/svgs';
import { cn } from '@/lib/cn';
import { IconButton } from '@/components/ui/IconButton';

export interface ChatInputProps {
  /** Input value */
  value: string;
  /** Callback when input value changes */
  onChange: (value: string) => void;
  /** Callback when send button is clicked */
  onSend: () => void;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /**
   * Whether the textarea itself is disabled.
   * Defaults to `disabled` for backwards compatibility.
   *
   * Why: on mobile Safari, a disabled textarea means the keyboard cannot open at all.
   * We often want to disable "Send" while still allowing typing.
   */
  inputDisabled?: boolean;
  /** Additional className for the container */
  className?: string;
  /** Callback for Enter key press */
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  /** Whether input is focused */
  isFocused?: boolean;
  /** Callback when input is focused */
  onFocus?: () => void;
  /** Callback when input loses focus */
  onBlur?: () => void;
}

export function ChatInput({
  value,
  onChange,
  onSend,
  placeholder = 'Input',
  disabled = false,
  inputDisabled,
  className,
  onKeyDown,
  isFocused: controlledIsFocused,
  onFocus,
  onBlur,
}: ChatInputProps) {
  const [internalIsFocused, setInternalIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Use controlled focus if provided, otherwise use internal state
  const isFocused = controlledIsFocused !== undefined ? controlledIsFocused : internalIsFocused;
  const hasValue = value.trim().length > 0;
  const isActive = hasValue && !disabled;
  const isTextAreaDisabled = inputDisabled ?? disabled;

  // Auto-resize textarea to fit content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [value]);

  const handleFocus = () => {
    if (controlledIsFocused === undefined) {
      setInternalIsFocused(true);
    }
    onFocus?.();
  };

  const handleBlur = () => {
    if (controlledIsFocused === undefined) {
      setInternalIsFocused(false);
    }
    onBlur?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && isActive) {
      e.preventDefault();
      onSend();
    }
    onKeyDown?.(e);
  };

  const handleSend = () => {
    if (isActive) {
      onSend();
    }
  };

  return (
    <div
      className={cn(
        'flex hover:bg-primary-hover active:bg-primary-hover items-center gap-2 bg-primary border rounded-full py-2 pr-2 transition-colors',
        // Border color: accent when focused, secondary otherwise
        isFocused ? 'border-accent' : 'border-secondary',
        className,
      )}
    >
      <textarea
        ref={textareaRef}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={isTextAreaDisabled}
        rows={1}
        style={{ resize: 'none' }}
        className={cn(
          'flex-1 text-body-2 ml-4 resize-none max-h-[60px] overflow-y-auto',
          'bg-transparent border-0 outline-none',
          // Text color: primary when has value, tertiary for placeholder
          hasValue ? 'text-primary' : 'text-tertiary',
          'placeholder:text-tertiary',
          'disabled:opacity-50 disabled:cursor-not-allowed',
        )}
      />
      <IconButton
        type={'primary'}
        size="medium"
        icon={IconSendMessage}
        className="p-2"
        onClick={handleSend}
        disabled={!isActive}
        aria-label="Send message"
      />
    </div>
  );
}
